<!--
  VoucherAssignTab — 바우처관리 › 도서 지정
  · 왼쪽: 진행 중 클래스(GET /classrooms?status=ongoing, assignments 요약) → 오른쪽: 선택 클래스의 지정 목록(GET /classrooms/{idx}/vouchers)
  · 지정 추가(AssignVoucherModal) · 기한/상한/메모 수정 · 종료 · 수강생별 상태
-->
<template>
  <div class="p-grid p-grid--assign">
    <a-card title="진행 중 클래스" :sub="`${filteredClassrooms.length}개`">
      <template #action>
        <a-btn size="sm" kind="ghost" @click="loadClassrooms">새로고침</a-btn>
      </template>
      <input v-model.trim="classQuery" type="text" class="p-input p-mb-10" placeholder="클래스명·강사명으로 찾기" />
      <div v-if="loadingClassrooms" class="p-loader"><div class="p-spinner" /></div>
      <a-empty v-else-if="!filteredClassrooms.length" title="진행 중 클래스가 없습니다" sub="기관 강사의 진행 중 클래스만 표시됩니다." />
      <div v-else class="p-pick">
        <button
          v-for="c in filteredClassrooms"
          :key="c.classroom_idx"
          type="button"
          class="p-pick__item"
          :class="{ 'is-active': selected && selected.classroom_idx === c.classroom_idx }"
          @click="select(c)"
        >
          <div class="p-pick__title">{{ c.name }}</div>
          <div class="p-pick__sub">
            {{ c.instructor_name || '-' }} · 수강생 {{ c.student_count == null ? 0 : c.student_count }}명 · 지정 {{ c.assignments ? c.assignments.length : 0 }}권
          </div>
        </button>
      </div>
    </a-card>

    <a-card v-if="!selected" title="도서 지정">
      <a-empty title="왼쪽에서 클래스를 선택하세요" sub="클래스마다 여러 권을 지정할 수 있습니다. 같은 도서는 진행 중 지정이 하나만 가능합니다." />
    </a-card>
    <a-card
      v-else
      :title="selected.name"
      :sub="`${selected.instructor_name || '-'} · ${fmtDate(selected.start_date)} ~ ${fmtDate(selected.end_date)} · 수강생 ${selected.student_count == null ? 0 : selected.student_count}명`"
    >
      <template #action>
        <div class="p-row-actions">
          <a-btn size="sm" kind="ghost" @click="goDetail(selected)">클래스 상세</a-btn>
          <a-btn size="sm" kind="primary" @click="showAssign = true">도서 지정 추가</a-btn>
        </div>
      </template>
      <div v-if="loadingAssignments" class="p-loader"><div class="p-spinner" /></div>
      <a-empty v-else-if="!assignments.length" title="지정된 도서가 없습니다" sub="‘도서 지정 추가’ 로 이 클래스 수강생에게 도서를 지급하세요." />
      <a-table v-else :columns="cols" :rows="assignments" row-key="idx">
        <template #cell-title="{ row }">
          <div class="p-cell-title">{{ row.title }}</div>
          <div class="p-cell-sub p-mono">{{ row.barcode }}</div>
        </template>
        <template #cell-grade="{ value }"><a-pill kind="yellow">{{ gradeLabel(value) }}</a-pill></template>
        <template #cell-register_until="{ value }"><span class="p-mono">{{ fmtDate(value) }}</span></template>
        <template #cell-max_registrations="{ value }"><span class="p-mono">{{ value == null ? '제한 없음' : value + '명' }}</span></template>
        <template #cell-registered="{ row }">
          <span class="p-mono">{{ row.registered_count == null ? 0 : row.registered_count }}<template v-if="row.eligible_count != null"> / 대상 {{ row.eligible_count }}</template></span>
          <div v-if="row.pending_count" class="p-cell-sub">처리 중 {{ row.pending_count }}</div>
        </template>
        <template #cell-unit_amount_estimate="{ value }"><span class="p-amount">{{ value == null ? '-' : fmtAmount(value, currency) }}</span></template>
        <template #cell-status="{ row }">
          <a-pill :kind="assignmentStatusKind(row.status)">{{ assignmentStatusLabel(row.status) }}</a-pill>
          <div v-if="row.status === 'CLOSED' && row.closed_at" class="p-cell-sub">{{ fmtDateTime(row.closed_at) }}</div>
        </template>
        <template #cell-actions="{ row }">
          <div class="p-row-actions">
            <a-btn size="sm" kind="ghost" @click="studentsTarget = row">수강생</a-btn>
            <a-btn v-if="row.status === 'ACTIVE'" size="sm" kind="ghost" @click="editTarget = row">수정</a-btn>
            <a-btn v-if="row.status === 'ACTIVE'" size="sm" kind="danger" :disabled="closing === row.idx" @click="closeAssignment(row)">종료</a-btn>
          </div>
        </template>
      </a-table>
      <div class="p-note">학생이 등록할 때 그 시점 기관가로 차감됩니다. 도서·등급을 바꾸려면 지정을 종료한 뒤 다시 지정하세요(이미 등록한 학생은 유지).</div>
    </a-card>

    <assign-voucher-modal
      v-if="showAssign && selected"
      :org-idx="orgIdx"
      :classroom="selected"
      :currency="currency"
      :existing-barcodes="activeBarcodes"
      @close="showAssign = false"
      @created="onCreated"
    />
    <assignment-edit-modal
      v-if="editTarget"
      :org-idx="orgIdx"
      :assignment="editTarget"
      @close="editTarget = null"
      @saved="onSaved"
    />
    <assignment-students-modal
      v-if="studentsTarget"
      :org-idx="orgIdx"
      :assignment="studentsTarget"
      :currency="currency"
      @close="studentsTarget = null"
    />
  </div>
</template>

<script>
import ACard from '@/components/Pineapple/ACard.vue';
import ATable from '@/components/Pineapple/ATable.vue';
import APill from '@/components/Pineapple/APill.vue';
import ABtn from '@/components/Pineapple/ABtn.vue';
import AEmpty from '@/components/Pineapple/AEmpty.vue';
import AssignVoucherModal from './AssignVoucherModal.vue';
import AssignmentEditModal from './AssignmentEditModal.vue';
import AssignmentStudentsModal from './AssignmentStudentsModal.vue';
import { fmtAmount, fmtDate, fmtDateTime, gradeLabel, assignmentStatusKind, assignmentStatusLabel, voucherErrorText } from '../orgUtils';

export default {
  name: 'VoucherAssignTab',
  components: { ACard, ATable, APill, ABtn, AEmpty, AssignVoucherModal, AssignmentEditModal, AssignmentStudentsModal },
  props: {
    orgIdx: { type: [Number, String], required: true },
    currency: { type: String, default: '' },
    initialClassroomIdx: { type: Number, default: null },
  },
  data() {
    return {
      classrooms: [],
      selected: null,
      assignments: [],
      loadingClassrooms: false,
      loadingAssignments: false,
      classQuery: '',
      showAssign: false,
      editTarget: null,
      studentsTarget: null,
      closing: null,
      cols: [
        { key: 'title',                label: '도서' },
        { key: 'grade',                label: '등급',       width: '90px' },
        { key: 'register_until',       label: '등록 기한',   width: '110px' },
        { key: 'max_registrations',    label: '인원 상한',   width: '90px',  align: 'right' },
        { key: 'registered',           label: '등록',       width: '120px', align: 'right' },
        { key: 'unit_amount_estimate', label: '1인 예상액',  width: '120px', align: 'right' },
        { key: 'status',               label: '상태',       width: '110px' },
        { key: 'actions',              label: '',           width: '190px', align: 'right' },
      ],
    };
  },
  computed: {
    filteredClassrooms() {
      const q = this.classQuery.toLowerCase();
      if (!q) return this.classrooms;
      return this.classrooms.filter(c =>
        String(c.name || '').toLowerCase().includes(q) || String(c.instructor_name || '').toLowerCase().includes(q));
    },
    activeBarcodes() {
      return this.assignments.filter(a => a.status === 'ACTIVE').map(a => a.barcode);
    },
  },
  async mounted() {
    await this.loadClassrooms();
    if (this.initialClassroomIdx) {
      const c = this.classrooms.find(x => x.classroom_idx === this.initialClassroomIdx);
      if (c) this.select(c);
    }
  },
  methods: {
    fmtAmount, fmtDate, fmtDateTime, gradeLabel, assignmentStatusKind, assignmentStatusLabel,
    async loadClassrooms() {
      this.loadingClassrooms = true;
      try {
        const res = await this.$axios({
          method: 'get',
          url: `/voucher/org/${this.orgIdx}/classrooms`,
          params: { status: 'ongoing' },
          loadingBar: false,
        });
        this.classrooms = Array.isArray(res.data) ? res.data : [];
        if (this.selected) {
          const again = this.classrooms.find(c => c.classroom_idx === this.selected.classroom_idx);
          this.selected = again || null;
        }
      } catch (e) {
        this.classrooms = [];
        this.$notifyVue({ type: 'error', title: voucherErrorText(e, '클래스 목록을 불러오지 못했습니다.') });
      } finally {
        this.loadingClassrooms = false;
      }
    },
    select(c) {
      this.selected = c;
      this.loadAssignments();
    },
    async loadAssignments() {
      if (!this.selected) return;
      this.loadingAssignments = true;
      try {
        const res = await this.$axios({
          method: 'get',
          url: `/voucher/org/${this.orgIdx}/classrooms/${this.selected.classroom_idx}/vouchers`,
          loadingBar: false,
        });
        this.assignments = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        this.assignments = [];
        this.$notifyVue({ type: 'error', title: voucherErrorText(e, '지정 목록을 불러오지 못했습니다.') });
      } finally {
        this.loadingAssignments = false;
      }
    },
    refreshAll() {
      this.loadAssignments();
      this.loadClassrooms();
      this.$store.dispatch('organization/refresh');
    },
    onCreated() {
      this.showAssign = false;
      this.refreshAll();
    },
    onSaved() {
      this.editTarget = null;
      this.refreshAll();
    },
    async closeAssignment(row) {
      const confirmation = await this.$swal({
        title: '지정 종료',
        text: `"${row.title}" (${gradeLabel(row.grade)}) 지정을 종료합니다. 아직 등록하지 않은 학생은 더 등록할 수 없고, 이미 등록한 학생의 도서는 유지됩니다.`,
        icon: 'warning',
        input: 'text',
        inputPlaceholder: '종료 사유 (선택)',
        showConfirmButton: true, showCancelButton: true,
        confirmButtonText: '종료', cancelButtonText: '취소',
        confirmButtonColor: '#C44A3A',
      });
      if (!confirmation.isConfirmed) return;
      this.closing = row.idx;
      try {
        const data = {};
        if (confirmation.value) data.reason = String(confirmation.value).trim();
        await this.$axios({
          method: 'post',
          url: `/voucher/org/${this.orgIdx}/class-vouchers/${row.idx}/close`,
          data,
          loadingBar: true,
        });
        this.$notifyVue({ type: 'success', title: `"${row.title}" 지정을 종료했습니다.` });
        this.refreshAll();
      } catch (e) {
        this.$notifyVue({ type: 'error', title: voucherErrorText(e, '지정을 종료하지 못했습니다.') });
      } finally {
        this.closing = null;
      }
    },
    goDetail(c) {
      this.$router.push({ name: 'AcademicClassesDetail', query: { idx: c.classroom_idx, view: 'org' } });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../_org.scss';

.p-mb-10 { margin-bottom: 10px; }
</style>
